import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLazyGetClientDashboardsQuery } from '@api/clientUserManagement';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import { DashboardSelectorSubmoduleItem } from '../../pages/Admin/UserManagementV2/types/dashboardSelector';
import { AppItem } from '../../pages/Admin/UserManagementV2/components/AppAccessSidepanel';

const useGetCompanyAccess = ({
  clientCode,
  enabled = true,
}: {
  clientCode: string;
  enabled?: boolean;
}) => {
  const dispatch = useDispatch();
  const [companyGames, setCompanyGames] = useState<AppItem[]>([]);
  const [companySubmodules, setCompanySubmodules] = useState<DashboardSelectorSubmoduleItem[]>([]);

  const [getClientDashboards, { isFetching: isLoading, isError }] =
    useLazyGetClientDashboardsQuery();

  useEffect(() => {
    setCompanyGames([]);
    setCompanySubmodules([]);
    if (clientCode && enabled) {
      void fetchCompanyAccess();
    }
  }, [clientCode, enabled]);

  const fetchCompanyAccess = async () => {
    if (!clientCode) return;
    setCompanyGames([]);
    setCompanySubmodules([]);
    try {
      const response = await getClientDashboards(clientCode).unwrap();
      const games: AppItem[] = (response?.data?.games ?? []).map((game) => ({
        id: game.gameId,
        gameId: game.gameId,
        name: game.name,
        imageUrl: game.iconUrl,
      }));

      setCompanyGames(games);
      const submodules: DashboardSelectorSubmoduleItem[] =
        response?.data?.submodules?.map((submodule) => ({
          submoduleId: submodule.submoduleId,
          name: submodule.submoduleName,
          submoduleType: submodule.submoduleType,
          editable: submodule.editable,
        })) ?? [];
      setCompanySubmodules(submodules);
    } catch {
      dispatch(
        setToastMessage({
          heading: 'Failed to load company access',
          message: 'Unable to fetch company dashboards. Please try again.',
          type: TOAST_TYPES.ERROR,
        }),
      );
      setCompanyGames([]);
      setCompanySubmodules([]);
      return [];
    }
  };

  return {
    fetchCompanyAccess,
    companySubmodules,
    setCompanySubmodules,
    companyGames,
    setCompanyGames,
    isLoading,
    isError,
  };
};

export default useGetCompanyAccess;
